import { AlertTriangle, VolumeX } from 'lucide-react';
import { useState } from 'react';

import {
  BasicTableSettings,
  RefreshableTableSettings,
  createPersistedStore,
  refreshableSettings,
} from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  Datatable,
  TableSettingsMenu,
  TableSettingsMenuAutoRefresh,
} from '@@/datatables';
import { Modal } from '@@/modals';
import { Button } from '@@/buttons';

import { createColumns } from './columns';
import { transformToActiveAlertRows } from './utils';
import { ActiveAlertRow } from './types';
import { SilenceAlertForm } from './SilenceAlertForm';
import { useActiveAlerts } from './useActiveAlerts';

type SortKey = keyof ActiveAlertRow;

const storageKey = 'portainer.active-alerts';
interface TableSettings extends BasicTableSettings, RefreshableTableSettings {}
const settingsStore = createPersistedStore<TableSettings>(
  storageKey,
  { id: 'startsAt', desc: true },
  (set) => ({
    ...refreshableSettings(set, 10_000),
  })
);

export function ActiveAlertsDatatable() {
  const tableState = useTableState(settingsStore, storageKey);
  const [alertsToSilence, setAlertsToSilence] = useState<
    ActiveAlertRow[] | null
  >(null);

  const alertsQuery = useActiveAlerts({
    refetchInterval: tableState.autoRefreshRateMS
      ? tableState.autoRefreshRateMS
      : undefined,
    select: transformToActiveAlertRows,
  });

  function handleSilence(alert: ActiveAlertRow) {
    setAlertsToSilence([alert]);
  }

  function handleSilenceCancel() {
    setAlertsToSilence(null);
  }

  function handleSilenceSave() {
    // TODO: Implement silence save logic
    setAlertsToSilence(null);
    // Optionally refresh the alerts query
    alertsQuery.refetch();
  }

  const columns = createColumns(handleSilence);

  return (
    <>
      <Datatable<ActiveAlertRow>
        dataset={alertsQuery.data || []}
        columns={columns}
        getGroupKey={(row, table) => {
          const sortedColumn = table
            .getAllColumns()
            .find((col) => col.getIsSorted());
          const columnDef = sortedColumn?.columnDef;

          // default sort
          if (!isColumnDef(columnDef)) {
            return row.original.environmentGroupName;
          }

          const sortKey = columnDef.accessorKey || columnDef.id;

          // default sort
          if (!isSortKey(sortKey)) {
            return row.original.environmentGroupName;
          }

          return `${row.original[sortKey]}`;
        }}
        isLoading={alertsQuery.isLoading}
        settingsManager={tableState}
        emptyContentLabel="No active alerts found"
        title="Active Alerts"
        isRowSelectable={() => true}
        titleIcon={AlertTriangle}
        renderTableActions={(selectedRows) => (
          <Button
            color="light"
            size="small"
            icon={VolumeX}
            onClick={() => setAlertsToSilence(selectedRows)}
            data-cy="active-alerts-datatable-bulk-silence-button"
            disabled={selectedRows.length === 0}
          >
            Silence
          </Button>
        )}
        renderTableSettings={() => (
          <TableSettingsMenu>
            <TableSettingsMenuAutoRefresh
              onChange={tableState.setAutoRefreshRate}
              value={tableState.autoRefreshRateMS}
            />
          </TableSettingsMenu>
        )}
        data-cy="active-alerts-datatable"
      />

      {alertsToSilence && (
        <Modal
          onDismiss={handleSilenceCancel}
          size="lg"
          aria-label="Silence Alert Dialog"
        >
          <SilenceAlertForm
            alerts={alertsToSilence}
            onSave={handleSilenceSave}
            onCancel={handleSilenceCancel}
          />
        </Modal>
      )}
    </>
  );
}

// does not work with ColumnDef typing for some reason?
function isColumnDef(def: unknown): def is { accessorKey?: string } {
  return (
    !!def && typeof def === 'object' && ('accessorKey' in def || 'id' in def)
  );
}

function isSortKey(key?: string): key is SortKey {
  return (
    !!key &&
    [
      'environmentGroupName',
      'environmentName',
      'alertName',
      'state',
      'severity',
      'startsAt',
      'updatedAt',
      'message',
    ].includes(key)
  );
}
